import type { AIChatSession, AIChatSessionMessage } from '@/types/generated'

/**
 * Determine whether the case linked to ``session`` may still be detached by the user.
 *
 * Mirrors :func:`assert_case_detach_allowed` on the backend
 * (``app/ai/services/chat/session/case_detach_validation.py``): once a user or assistant
 * message has been recorded at or after ``case_linked_at``, the case is permanently bound
 * to the session and the remove-case affordance must be hidden.
 *
 * This check operates purely on the authoritative session payload returned by the API —
 * callers no longer have to track a transient "did I just send a message with the case
 * attached" flag to derive the lock state after a reload or side-panel switch.
 *
 * :param session: Chat session as returned from the API (must carry ``messages``).
 * :return: ``true`` when the session has any persisted user/assistant message whose
 *     ``created_at`` falls at or after ``case_linked_at``. ``false`` when the case is
 *     unlinked or no qualifying message exists yet. Fails closed (``true``) if a message
 *     lacks a parseable timestamp, matching the backend's conservative behaviour.
 */
export function hasCaseContextMessages(session: AIChatSession | null | undefined): boolean {
  if (!session?.case_linked_at) return false

  const linkedAtMs = Date.parse(session.case_linked_at)
  if (Number.isNaN(linkedAtMs)) return true

  const messages = (session.messages ?? []) as AIChatSessionMessage[]
  for (const msg of messages) {
    if (msg.role !== 'user' && msg.role !== 'assistant') continue
    const rawCreatedAt = msg.created_at
    if (!rawCreatedAt) return true
    const ts = Date.parse(rawCreatedAt)
    if (Number.isNaN(ts)) return true
    if (ts >= linkedAtMs) return true
  }
  return false
}
